<template>
  <div
    class="artwork-dimensions"
    :class="{
      'artwork-dimensions--with-border': showBorderUI,
      'artwork-dimensions--with-border-invalid': isInvalid
    }"
  >
    <div v-if="label && !showBorderUI" class="artwork-dimensions__label">{{ label }}</div>
    <StepNumber
      v-if="showBorderUI"
      :step-number="isPuff ? 3 : 2"
      content="Set Design Size"
    ></StepNumber>
    <div class="artwork-dimensions__form-number-row">
      <div class="artwork-dimensions__form-number-group">
        <label
          class="artwork-dimensions__number-label"
          :class="{
            'artwork-dimensions__number-label--gang': printAsIs
          }"
          :for="widthId"
        >
          Width <template v-if="!showBorderUI"> (inches)</template>
        </label>
        <div
          class="artwork-dimensions__number-control"
          :class="{
            'artwork-dimensions__number-control--gang-lock': gangPadlock && !!width
          }"
        >
          <input
            :id="widthId"
            :value="width"
            class="artwork-dimensions__number-field js-dtf-width-input"
            :class="{
              'artwork-dimensions__number-field--invalid': validationError && !isWidthValid
            }"
            max="100"
            min="0.1"
            :name="widthId"
            step=".01"
            type="number"
            data-hj-allow
            :disabled="disabled"
            :readonly="readonly || !!widthDefault"
            @click="handleInputClick"
            @input="handleWidthInput"
            @change="handleWidthInput"
            @focusin="handleFocusIn"
            @blur="onBlur"
          />
        </div>
      </div>

      <UseSvg
        icon="cross"
        class="artwork-dimensions__multiplier"
        :class="{
          'artwork-dimensions__multiplier--gang': printAsIs
        }"
      />

      <div class="artwork-dimensions__form-number-group">
        <label
          class="artwork-dimensions__number-label"
          :class="{
            'artwork-dimensions__number-label--gang': printAsIs
          }"
          :for="heightId"
        >
          {{ heightLabel }}
        </label>
        <div
          class="artwork-dimensions__number-control"
          :class="{
            'artwork-dimensions__number-control--gang-lock': lockHeight
          }"
        >
          <input
            :id="heightId"
            :value="height"
            class="artwork-dimensions__number-field js-dtf-height-input"
            :class="{
              'artwork-dimensions__number-field--invalid': validationError && !isHeightValid
            }"
            max="100"
            min="0.1"
            :name="heightId"
            step=".01"
            type="number"
            data-hj-allow
            :disabled="disabled"
            :readonly="readonlyHeight"
            @click="handleInputClick"
            @input="handleHeightInput"
            @change="handleHeightInput"
            @focusin="handleFocusIn"
            @blur="onBlur"
          />
        </div>
      </div>

      <UseSvg icon="cross" class="artwork-dimensions__multiplier" />

      <div
        class="artwork-dimensions__form-number-group artwork-dimensions__form-number-group--price"
      >
        <div
          v-if="printAsIs"
          class="artwork-dimensions__number-label artwork-dimensions__number-label--gang"
        >
          Linear foot
          <br />
          price
        </div>
        <div
          v-else
          class="artwork-dimensions__number-label"
          :class="{ 'artwork-dimensions__number-label--light': showBorderUI }"
        >
          Price / in²
        </div>
        <div class="artwork-dimensions__static-input">{{ n(priceInch, "currencyDetailed") }}</div>
      </div>

      <div class="artwork-dimensions__equal-sign">=</div>

      <div
        class="artwork-dimensions__form-number-group artwork-dimensions__form-number-group--price"
      >
        <div
          v-if="printAsIs"
          class="artwork-dimensions__number-label artwork-dimensions__number-label--gang"
        >
          Price <br />
          per Gang Sheet
        </div>
        <div
          v-else
          class="artwork-dimensions__number-label"
          :class="{ 'artwork-dimensions__number-label--light': showBorderUI }"
        >
          Total price
        </div>
        <div class="artwork-dimensions__static-input">{{ n(totalPrice, "currency") }}</div>
      </div>
    </div>
    <div v-if="showPaddingNote" class="artwork-dimensions__free-note">
      {{ paddingNote }}
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from "pinia";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import {
  trackRudderstackImageEdited,
  trackRudderstackImageUploadError
} from "common/utils/rudderstack/tracker";
import { getStickerDimensions } from "common/utils/stickers";
import UAParser from "ua-parser-js";
import isEqual from "lodash.isequal";
import UseSvg from "common/vue/components/UseSvg.vue";
import StepNumber from "./common/StepNumber.vue";
import { n } from "../vue-cart/helpers/currency";
import { useSpanglesStore, SPANGLE_MODES } from "./spangles-module";
import { useDtfStore } from "./store";
import {
  MIN_DIMENSION_SIZE,
  MIN_DPI,
  PRINT_AS_IS_MIN_DPI,
  PRINT_AS_IS_MAX_HEIGHT,
  PRINT_AS_IS_MIN_WIDTH,
  MAX_SIZE_FIELD_VALUE,
  UPSCALE_THRESHOLD,
  DEFAULT_VALUES,
  GANG_DEFAULT_VALUES,
  MIN_DIMENSION_PUFF
} from "./common/constants";

export default {
  name: "ArtworkDimensions",
  components: { UseSvg, StepNumber },
  props: {
    artwork: {
      type: Object,
      default: null
    },
    index: {
      type: Number,
      default: 0
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    widthDefault: {
      type: String,
      default: null
    },
    currentStore: {
      type: String,
      default: null
    },
    isMultiUpload: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    return { n };
  },
  data() {
    const dimensions = this.printAsIs ? GANG_DEFAULT_VALUES : DEFAULT_VALUES;

    return {
      newWidth: null,
      newHeight: null,
      oldWidth: null,
      oldHeight: null,
      isHeightValid: true,
      isWidthValid: true,
      previousData: {
        width: dimensions.width,
        height: dimensions.height
      },
      sizeChanged: false
    };
  },
  computed: {
    ...mapState(useDtfStore, [
      "variantId",
      "simulation",
      "printAsIs",
      "isPuff",
      "pdpType",
      "catalogNumber",
      "dpiInstructionsUrl",
      "showModal",
      "dtfMaxWidth",
      "dtfMaxHeight",
      "dtfMinWidth",
      "dtfMinHeight",
      "errorTypes",
      "isSpangle",
      "sticker",
      "businessLine",
      "showStepsUI",
      "isStandardArtwork",
      "isProColor",
      "artworks",
      "preCut"
    ]),
    ...mapState(useSpanglesStore, ["spangleSizeEdited", "isInitialSpangle"]),
    validationError() {
      return this.artwork.errorMessage;
    },
    validationErrorType() {
      return this.artwork.errorType;
    },
    gangPadlock() {
      return this.printAsIs && !this.showModal;
    },
    lockHeight() {
      return this.gangPadlock && !!this.metadata;
    },
    readonlyHeight() {
      if (this.showModal && this.printAsIs) return this.readonly;
      if (!this.lockHeight) return false;
      return this.readonly;
    },
    minDpi() {
      if (this.printAsIs) return PRINT_AS_IS_MIN_DPI;
      return this.metadata?.upscalingEnabled ? Number(this.metadata.qualityUpscaleDpi) : MIN_DPI;
    },
    pricing() {
      return this.artwork?.pricing || this.simulation;
    },
    priceInch() {
      if (this.pricing?.pricePerInch) {
        return Number(this.pricing.pricePerInch).toFixed(4);
      }
      return null;
    },
    totalPrice() {
      if (this.pricing?.transferPrice) {
        return Number(this.pricing.transferPrice).toFixed(2) || "";
      }
      return null;
    },
    widthId() {
      return `artwork[${this.index}][variants][${this.variantId}][width]`;
    },
    heightId() {
      return `artwork[${this.index}][variants][${this.variantId}][height]`;
    },
    metadata() {
      const { removeBackground, metadata, withBgMetadata } = this.artwork || {};

      if (!withBgMetadata || Object.keys(withBgMetadata).length === 0) {
        return metadata;
      }

      return removeBackground ? metadata : withBgMetadata;
    },
    width() {
      return this.artwork?.previewMetadata?.widthInch || this.artwork?.width || this.widthDefault;
    },
    height() {
      const artworkHeight = this.artwork?.height;

      if (this.printAsIs && !!this.metadata) {
        return this.artwork.lengthFoot;
      }

      return artworkHeight;
    },
    heightLabel() {
      const unit = this.printAsIs ? "" : "(inches)";
      const label = this.printAsIs ? "Length (feet)" : "Height";
      return this.showBorderUI ? label : `${label} ${unit}`;
    },
    showPaddingNote() {
      return !this.isSpangle && !this.printAsIs && !this.isPuff;
    },
    paddingNote() {
      return this.sticker
        ? "Padding for easy peeling is added for free"
        : "Padding for easy cutting is added for free";
    },
    heightName() {
      return this.printAsIs ? "length" : "height";
    },
    minWidthSetting() {
      return this.printAsIs ? PRINT_AS_IS_MIN_WIDTH : this.dtfMinWidth;
    },
    isWidthAndHeightValid() {
      return (
        this.width !== "" &&
        Number(this.width) >= this.minWidth &&
        this.height !== "" &&
        Number(this.height) >= this.minHeight
      );
    },
    fitsGangSheet() {
      return (
        this.width !== "" &&
        this.height !== "" &&
        Math.min(this.width, this.height) <= this.metadata?.maxSheetWidth &&
        Math.max(this.width, this.height) <= this.metadata?.maxSheetHeight
      );
    },
    dpi() {
      return Math.round(this.metadata.width / this.width);
    },
    imageMetadataInfo() {
      if (!this.metadata) return null;

      return {
        aspect_ratio: this.metadata.aspectRatio,
        resolution_width: this.metadata.width,
        resolution_height: this.metadata.height,
        width: this.width,
        height: this.height,
        max_width: this.metadata.maxWidth,
        max_height: this.metadata.maxHeight,
        max_sheet_width: this.metadata.maxSheetWidth,
        max_sheet_height: this.metadata.maxSheetHeight,
        min_dpi: this.minDpi,
        file_name: this.artwork.name,
        file_size: this.artwork.fileSize,
        file_type: this.artwork.fileType
      };
    },
    isUpscaleThresholdExceeded() {
      if (this.isSpangle) return false;

      const { originalWidth, originalHeight, width, height } = this.artwork;

      if (!originalWidth || !originalHeight || !width || !height) return false;

      const newWidth = parseFloat(width);
      const newHeight = parseFloat(height);

      const upscaleFactorWidth = (newWidth / originalWidth).toFixed(2);
      const upscaleFactorHeight = (newHeight / originalHeight).toFixed(2);
      const upscaleFactor = Math.min(upscaleFactorWidth, upscaleFactorHeight);

      return upscaleFactor > UPSCALE_THRESHOLD;
    },
    showSpangleEditSize() {
      return this.isSpangle && !this.isInitialSpangle;
    },
    minSizeForProduct() {
      // Skip validation if dimensions are not yet loaded
      if (
        this.width === null ||
        this.width === undefined ||
        this.width === "" ||
        this.height === null ||
        this.height === undefined ||
        this.height === ""
      ) {
        return true;
      }

      const widthNum = Number(this.width);
      const heightNum = Number(this.height);

      // Skip validation if dimensions are not valid numbers
      if (Number.isNaN(widthNum) || Number.isNaN(heightNum)) {
        return true;
      }

      if (this.isSpangle) {
        return widthNum >= this.dtfMinWidth && heightNum >= this.dtfMinHeight;
      }

      if (this.sticker) {
        return widthNum >= MIN_DIMENSION_SIZE && heightNum >= MIN_DIMENSION_SIZE;
      }

      if (this.isPuff) {
        return widthNum >= MIN_DIMENSION_PUFF.width && heightNum >= MIN_DIMENSION_PUFF.height;
      }

      return widthNum >= this.dtfMinWidth && heightNum >= this.dtfMinHeight;
    },
    label() {
      if (this.isPuff || this.sticker) {
        return "Select size";
      }
      if (this.isSpangle) {
        return "Design size";
      }

      return null;
    },
    isInvalid() {
      return (
        this.showBorderUI && this.validationError && (!this.isWidthValid || !this.isHeightValid)
      );
    },
    showBorderUI() {
      return this.showStepsUI && !this.isMultiUpload;
    },
    editType() {
      if (this.sizeChanged) {
        return "Size Adjustment";
      }
      if (this.artwork.enhanceImage) {
        return "Jiffy Print Perfect";
      }

      return "Print as uploaded";
    },
    minWidth() {
      return this.isPuff ? MIN_DIMENSION_PUFF.width : MIN_DIMENSION_SIZE;
    },
    minHeight() {
      return this.isPuff ? MIN_DIMENSION_PUFF.height : MIN_DIMENSION_SIZE;
    },
    minDimensions() {
      if (this.isPuff) {
        return {
          width: MIN_DIMENSION_PUFF.width,
          height: MIN_DIMENSION_PUFF.height
        };
      }

      return {
        width: this.minWidthSetting,
        height: this.dtfMinHeight
      };
    }
  },
  watch: {
    // eslint-disable-next-line max-lines-per-function
    artwork(newArtwork, oldArtwork) {
      if (newArtwork.isFile || newArtwork.isSimulated) return;
      if (!!newArtwork.metadata?.errors && !newArtwork.isValid) return;

      // Handle artwork removal by verifying the artwork index
      if (newArtwork.originalIndex && newArtwork.originalIndex !== this.index) {
        // Update artwork's index reference to match current position
        this.updateArtworkValues({
          index: newArtwork.originalIndex,
          originalIndex: this.index
        });
        this.getArtworksPricing();
        return;
      }

      const newArtworkSize = `${newArtwork.width}${newArtwork.height}`;
      const oldArtworkSize = `${oldArtwork.width}${oldArtwork.height}`;
      const triggerValidateOnSpangleSizeEdit =
        this.isSpangle && this.spangleSizeEdited && newArtworkSize !== oldArtworkSize;
      const enhancementSwitched =
        this.isStandardArtwork && newArtwork.enhanceImage !== oldArtwork.enhanceImage;

      if (
        (oldArtwork.isLoading && !newArtwork.isLoading) ||
        oldArtwork.removeBackground !== newArtwork.removeBackground ||
        triggerValidateOnSpangleSizeEdit ||
        enhancementSwitched
      ) {
        if (this.sticker) {
          const skipDefaultCheck = newArtwork.originalWidth !== newArtwork.width;
          this.previousData = getStickerDimensions(
            newArtwork.width,
            newArtwork.height,
            skipDefaultCheck
          );
          this.updateArtworkValues({ index: this.artwork.originalIndex, ...this.previousData });
          this.getArtworksPricing();
        } else {
          this.previousData = {
            width: newArtwork.width,
            height: newArtwork.height
          };
        }
        this.validateValues(true);
      }

      if (
        newArtwork.pricing &&
        oldArtwork.pricing &&
        !isEqual(newArtwork.pricing.unitPrice, oldArtwork.pricing.unitPrice)
      ) {
        this.updateArtworksPricingAndTrackSizeChange(
          oldArtwork.pricing.unitPrice,
          newArtwork.pricing.unitPrice
        );
      }
    }
  },
  methods: {
    ...mapActions(useDtfStore, ["updateArtworkValues", "setValidationErrorMessage"]),
    ...mapActions(useSpanglesStore, ["setSpangleMode"]),
    ...mapActions(useDtfStore, [
      "getArtworksPricing",
      "updatePdpDates",
      "updatePreviewDpi",
      "downscaleOriginalImage"
    ]),
    handleInput(dimensionType, e) {
      let { value } = e.target;
      if (this.printAsIs && dimensionType === "height" && value > MAX_SIZE_FIELD_VALUE) {
        // using Math.min(x, y) prevents decimals
        value = MAX_SIZE_FIELD_VALUE;
      }
      this.autoFillInputs(dimensionType, value);
    },

    handleInputClick(e) {
      // Done so that the input field blur works properly on firefox when using the input arrows.
      e.target.focus();
    },

    handleFocusIn() {
      this.oldWidth = this.newWidth;
      this.oldHeight = this.newHeight;
    },

    handleWidthInput(e) {
      this.handleInput("width", e);
    },

    handleHeightInput(e) {
      this.handleInput("height", e);
    },

    autoFillInputs(field, value) {
      if (!this.artwork) return;

      let newDimensions = {
        width: this.artwork.width,
        height: this.artwork.height
      };

      if (Number(value) > MAX_SIZE_FIELD_VALUE) {
        /* eslint-disable no-param-reassign */
        value = MAX_SIZE_FIELD_VALUE;
      }

      if (field === "width") {
        newDimensions = {
          width: value,
          height: this.getProportionalEdge(value, "height"),
          widthEdited: true
        };
      }

      if (field === "height") {
        newDimensions = {
          width: this.getProportionalEdge(value, "width"),
          height: value
        };
      }

      this.newWidth = newDimensions.width;
      this.newHeight = newDimensions.height;

      this.updateArtworkValues({ index: this.artwork.originalIndex, ...newDimensions });
      this.updatePreviewDpi({ artworkId: this.artwork.id });
      this.downscaleOriginalImage({ artworkId: this.artwork.id });
    },

    getProportionalEdge(value, field) {
      if (!this.metadata?.aspectRatio) return this.artwork[field];

      const factor = field === "height" ? 1 / this.metadata.aspectRatio : this.metadata.aspectRatio;
      const otherEdge = Math.round(value * factor * 100) / 100;

      return otherEdge.toFixed(2) || null;
    },

    sendValidationWarning(sendEvent) {
      if (!sendEvent) return;

      const warning = this.artwork.metadata?.warnings?.keys[0];
      // only for gang sheet uploads
      if (!this.printAsIs || !warning) return;

      this.sendWarningEvent("image_validation_warning", warning);
    },

    validateValues(sendEvent = false) {
      this.setValidationError(sendEvent);
      if (!this.metadata) {
        this.isHeightValid = this.isWidthAndHeightValid;
        this.isWidthValid = this.isWidthAndHeightValid;
        return;
      }

      this.sendValidationWarning(sendEvent);

      const widthConstraint =
        this.metadata.aspectRatio > 1 ? this.metadata.maxSheetHeight : this.metadata.maxSheetWidth;
      const heightConstraint =
        this.metadata.aspectRatio > 1 ? this.metadata.maxSheetWidth : this.metadata.maxSheetHeight;

      this.isWidthValid = this.fitsGangSheet
        ? this.isWidthAndHeightValid
        : this.width !== "" && this.width <= widthConstraint;

      this.isHeightValid = this.fitsGangSheet
        ? this.isWidthAndHeightValid
        : this.height !== "" && this.height <= heightConstraint;
    },

    setValidationError(sendEvent) {
      if (this.printAsIs && !this.artwork.isSimulated) return;

      // Check for errors
      const isInvalid =
        this.invalidWidthValidation(sendEvent) ||
        this.invalidHeightValidation(sendEvent) ||
        this.negativeValuesValidation(sendEvent) ||
        this.minPrintSizeValidation(sendEvent) ||
        this.maxPrintSizeValidation(sendEvent) ||
        this.maxPrintSizePrintAsIsValidation(sendEvent) ||
        this.upscaleThresholdExceeded(sendEvent);

      // If there's no error neither warning, clear error message on the next run.
      if (!isInvalid) {
        this.setValidationErrorMessage({ index: this.index, message: null, type: null });
      }
    },

    invalidWidthValidation(sendEvent = false) {
      if (this.width !== "") return false;
      if (this.validationErrorType !== this.errorTypes.INVALID_WIDTH && sendEvent) {
        this.sendValidationEvent("image_validation_error", "invalid_width", {});
      }
      this.setValidationErrorMessage({
        index: this.index,
        message: "Please enter a valid width.",
        type: this.errorTypes.INVALID_WIDTH
      });
      return true;
    },

    invalidHeightValidation(sendEvent = false) {
      if (this.height !== "") return false;
      if (this.validationErrorType !== this.errorTypes.INVALID_HEIGHT && sendEvent) {
        this.sendValidationEvent("image_validation_error", "invalid_height", {});
      }
      this.setValidationErrorMessage({
        index: this.index,
        message: `Please enter a valid ${this.heightName}.`,
        type: this.errorTypes.INVALID_HEIGHT
      });
      return true;
    },

    negativeValuesValidation(sendEvent = false) {
      if (this.width >= 0 && this.height >= 0) return false;
      if (this.validationErrorType !== this.errorTypes.NEGATIVE_VALUES && sendEvent) {
        this.sendValidationEvent("image_validation_error", "negative_values", {
          width: this.width,
          height: this.height
        });
      }
      this.setValidationErrorMessage({
        index: this.index,
        message: "Negative values are not permitted.",
        type: this.errorTypes.NEGATIVE_VALUES
      });
      return true;
    },

    minPrintSizeValidation(sendEvent = false) {
      if (this.minSizeForProduct) return false;
      if (this.validationErrorType !== this.errorTypes.MIN_PRINT_SIZE && sendEvent) {
        if (this.showSpangleEditSize) {
          this.setSpangleMode(SPANGLE_MODES.editSize);
        }
        this.sendValidationEvent("image_validation_error", "min_print_size", {});
      }
      const unit = this.printAsIs ? "'" : '"';

      this.setValidationErrorMessage({
        index: this.index,
        message: `The minimum print size is ${this.minDimensions.width}" x ${this.minDimensions.height}${unit}`,
        type: this.errorTypes.MIN_PRINT_SIZE
      });
      return true;
    },

    maxPrintSizeValidation(sendEvent = false) {
      if (this.printAsIs || this.fitsGangSheet) return false;

      let maxSheetWidth = this.dtfMaxWidth;
      let maxSheetHeight = this.dtfMaxHeight;

      if (this.metadata && this.metadata.maxSheetHeight && this.metadata.maxSheetWidth) {
        maxSheetWidth =
          this.aspectRatio > 1 ? this.metadata.maxSheetHeight : this.metadata.maxSheetWidth;
        maxSheetHeight =
          this.aspectRatio > 1 ? this.metadata.maxSheetWidth : this.metadata.maxSheetHeight;
      }

      if (Number(this.width) <= maxSheetWidth && Number(this.height) <= maxSheetHeight) {
        return false;
      }

      if (
        this.sticker &&
        Number(this.previousData.width) <= maxSheetWidth &&
        Number(this.previousData.height) <= maxSheetHeight
      ) {
        return false;
      }

      if (this.validationErrorType !== this.errorTypes.MAX_PRINT_SIZE && sendEvent) {
        if (this.showSpangleEditSize) {
          this.setSpangleMode(SPANGLE_MODES.editSize);
        }
        this.sendValidationEvent("image_validation_error", "max_print_size", {});
      }
      const message = `The maximum print size is ${maxSheetWidth}" x ${maxSheetHeight}"`;

      this.setValidationErrorMessage({
        index: this.index,
        type: this.errorTypes.MAX_PRINT_SIZE,
        message
      });
      return true;
    },

    upscaleThresholdExceeded(sendEvent = false) {
      if (this.printAsIs || !this.isUpscaleThresholdExceeded) return false;

      if (this.validationErrorType !== this.errorTypes.UPSCALE_THRESHOLD_EXCEEDED && sendEvent) {
        this.sendValidationEvent("image_validation_error", "upscale_threshold_exceeded", {});
      }
      this.setValidationErrorMessage({
        index: this.index,
        message: "upscale_threshold_exceeded",
        type: this.errorTypes.UPSCALE_THRESHOLD_EXCEEDED
      });

      // Chat on DTF pages is temporarily disabled https://dekeo.atlassian.net/browse/MN-1807
      // this.openZendeskChat();

      return true;
    },

    maxPrintSizePrintAsIsValidation() {
      if (
        this.printAsIs &&
        this.height > (this.metadata?.maxSheetHeight || PRINT_AS_IS_MAX_HEIGHT)
      ) {
        this.setValidationErrorMessage({
          index: this.index,
          message: `The maximum print size is ${PRINT_AS_IS_MIN_WIDTH}" x ${PRINT_AS_IS_MAX_HEIGHT}'`,
          type: null
        });
        return true;
      }
      return false;
    },

    sendValidationEvent(eventType, errorType, data) {
      let metadata = {
        ...data,
        has_image: !this.artwork.isFile && !this.artwork.isSimulated,
        pdp_type: this.pdpType,
        business_line: this.businessLine,
        page: "pdp"
      };

      if (!this.artwork.isFile) {
        metadata = { ...metadata, ...this.imageMetadataInfo };
      }

      submitDtfEvent(eventType, {
        type: errorType,
        ...metadata
      });
      trackRudderstackImageUploadError({
        error_message: errorType,
        business_line: this.businessLine,
        location: "pdp",
        pre_cut_transfers: this.preCut
      });
    },

    sendWarningEvent(eventType, warningType) {
      const metadata = {
        ...this.imageMetadataInfo,
        has_image: !this.artwork.isFile && !this.artwork.isSimulated,
        pdp_type: this.pdpType,
        business_line: this.businessLine,
        page: "pdp"
      };

      submitDtfEvent(eventType, {
        type: warningType,
        ...metadata
      });
    },

    async updateArtworksPricingAndTrackSizeChange(oldArtworkPrice, newArtworkPrice) {
      const detectProductLine = obj => {
        if (obj.sticker) return "sticker";
        if (obj.isProColor) return "pro color";
        if (obj.isSpangle) return "spangle";

        return "dtf";
      };

      const eventPayload = {
        // eslint-disable-next-line no-nested-ternary
        product_line: detectProductLine(this),
        business_line: this.businessLine,
        artwork: this.artwork,
        edit_type: this.editType,
        artworks_count: this.artworks.length,
        location: "pdp",
        old_price: oldArtworkPrice,
        new_price: newArtworkPrice,
        pre_cut_transfers: this.preCut
      };

      this.sizeChanged = false;

      trackRudderstackImageEdited(eventPayload);

      // Reindex remaining artworks to maintain consistent originalIndex values
      const dtfStore = useDtfStore();
      dtfStore.reindexArtworks();
    },

    trackSizeChanges() {
      if (this.newWidth === this.oldWidth && this.newHeight === this.oldHeight) return;

      let sizeChange;

      const area = this.newWidth * this.newHeight;
      const oldArea = this.oldWidth * this.oldHeight;
      if (area === 0 && oldArea === 0) {
        sizeChange = "none";
      } else if (area > oldArea) {
        sizeChange = "larger";
      } else {
        sizeChange = "smaller";
      }
      const sizePercentChange =
        oldArea === 0 ? 0 : Math.round((Math.abs(area - oldArea) / oldArea) * 100);

      submitDtfEvent("size_modified", {
        width: this.newWidth,
        height: this.newHeight,
        previous_width: this.oldWidth ?? this.width,
        previous_height: this.oldHeight ?? this.height,
        size_change: sizeChange,
        business_line: this.businessLine,
        size_percent_change: sizePercentChange,
        has_image: !this.simulation,
        page: "pdp",
        pdp_type: this.pdpType
      });
    },

    onBlur() {
      // compare object instead of individual width & height
      const currentData = {
        width: this.width,
        height: this.height
      };

      if (isEqual(currentData, this.previousData)) {
        return;
      }

      this.validateValues(true);

      if (this.isWidthAndHeightValid) {
        this.previousData = currentData;
        this.getArtworksPricing();
        this.updatePdpDates();
        this.trackSizeChanges();
        this.sizeChanged = true;
      }
    },

    openZendeskChat() {
      if (!window.zE) return;

      // desktop only
      const device = new UAParser().getDevice();
      if (["mobile", "tablet"].includes(device.type)) return;

      window.zE(() => {
        window.zE("messenger", "open");
      });
    }
  }
};
</script>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

.artwork-dimensions {
  $this: &;

  margin-bottom: 16px;
  border-bottom: 1px solid $gray-border;
  padding-bottom: 10px;

  &--with-border {
    border: 1px solid $gray-border;
    padding: 10px 15px;
    border-radius: 8px;
    box-shadow: 0px 25px 30px 0px rgba($black, 0.05);
    background-color: $white;

    #{$this}__form-number-row {
      margin-top: 3px;
      justify-content: space-between;
    }

    #{$this}__form-number-group {
      flex-direction: column-reverse;
    }

    #{$this}__number-label {
      margin-top: 5px;
      margin-bottom: 0;
    }

    #{$this}__multiplier,
    #{$this}__equal-sign {
      align-self: center;
      margin-bottom: 20px;
    }
  }

  &--with-border-invalid {
    border-color: $red-border;
  }

  &__label {
    margin: 0 0 16px;
    color: $text-black;
    font-size: 16px;
    font-weight: 700;
  }

  &__title {
    margin-bottom: 10px;
    font-weight: bold;
  }

  &__label {
    color: $text-black;
    font-size: 16px;
    font-weight: 700;
    margin-bottom: 16px;
  }

  &__multiplier {
    align-self: flex-end;
    margin-bottom: 13px;
    padding: 5px;
    width: 18px;
    height: 18px;
    background-color: $section-grey;
    border-radius: 50%;

    &--gang {
      background-color: $white;
      color: $white;
    }
  }

  &__equal-sign {
    align-self: flex-end;
    margin-bottom: 13px;
    padding: 5px;
    width: 18px;
    height: 18px;
    background-color: $section-grey;
    border-radius: 50%;
    line-height: 9px;
  }

  &__form-number-row {
    display: flex;
    gap: 4px;

    @include with-tablet {
      gap: 7px;
    }
  }

  &__form-number-group {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 4px;

    &--price {
      min-width: 64px;
      flex: unset;
    }
  }

  &__number-control--gang-lock {
    #{$this}__number-field {
      @include with-large-desktop {
        background-image: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTgiIHZpZXdCb3g9IjAgMCAxNiAxOCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEyLjE2NjggNy4zMzMzM1Y1LjY2NjY3QzEyLjE2NjggMy4zNjU0OCAxMC4zMDEzIDEuNSA4LjAwMDE2IDEuNUM1LjY5ODk4IDEuNSAzLjgzMzUgMy4zNjU0OCAzLjgzMzUgNS42NjY2N1Y3LjMzMzMzTTguMDAwMTYgMTEuMDgzM1YxMi43NU01LjMzMzUgMTYuNUgxMC42NjY4QzEyLjA2NyAxNi41IDEyLjc2NyAxNi41IDEzLjMwMTggMTYuMjI3NUMxMy43NzIyIDE1Ljk4NzggMTQuMTU0NyAxNS42MDU0IDE0LjM5NDMgMTUuMTM1QzE0LjY2NjggMTQuNjAwMiAxNC42NjY4IDEzLjkwMDEgMTQuNjY2OCAxMi41VjExLjMzMzNDMTQuNjY2OCA5LjkzMzIgMTQuNjY2OCA5LjIzMzE0IDE0LjM5NDMgOC42OTgzNkMxNC4xNTQ3IDguMjI3OTUgMTMuNzcyMiA3Ljg0NTUgMTMuMzAxOCA3LjYwNTgyQzEyLjc2NyA3LjMzMzMzIDEyLjA2NyA3LjMzMzMzIDEwLjY2NjggNy4zMzMzM0g1LjMzMzVDMy45MzMzNiA3LjMzMzMzIDMuMjMzMyA3LjMzMzMzIDIuNjk4NTIgNy42MDU4MkMyLjIyODExIDcuODQ1NSAxLjg0NTY2IDguMjI3OTUgMS42MDU5OCA4LjY5ODM2QzEuMzMzNSA5LjIzMzE0IDEuMzMzNSA5LjkzMzIgMS4zMzM1IDExLjMzMzNWMTIuNUMxLjMzMzUgMTMuOTAwMSAxLjMzMzUgMTQuNjAwMiAxLjYwNTk4IDE1LjEzNUMxLjg0NTY2IDE1LjYwNTQgMi4yMjgxMSAxNS45ODc4IDIuNjk4NTIgMTYuMjI3NUMzLjIzMzMgMTYuNSAzLjkzMzM2IDE2LjUgNS4zMzM1IDE2LjVaIiBzdHJva2U9IiM3NTc1NzUiIHN0cm9rZS13aWR0aD0iMS41IiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPC9zdmc+Cg==);
        background-repeat: no-repeat;
        background-position: 30%;
        padding-left: 18px;
      }
    }
  }

  &__number-label {
    display: block;
    margin-bottom: 10px;
    font-size: 12px;
    font-weight: 700;
    line-height: 13px;
    text-align: center;

    &--gang {
      margin-bottom: 0;
    }

    &--light {
      font-weight: 400;
    }
  }

  &__number-field {
    width: 100%;
    height: 44px;
    border: 1px solid $gray-label;
    border-radius: 3px;
    text-align: center;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      display: none;
    }

    &:read-only {
      background-color: $section-grey;
      outline: none;
      border: 1px solid $section-grey-border;
    }

    &:disabled {
      background: $section-grey;
    }

    &:focus {
      border: 2px solid $black;
    }

    &:focus:read-only {
      outline: none;
      border: 1px solid $section-grey-border;
    }

    &--invalid {
      border-color: $red-light;

      &:focus {
        border-color: $red-light;
        border-width: 2px;
        outline: none;
      }

      &:focus:read-only {
        outline: none;
        border: none;
      }
    }
  }

  &__info {
    padding: 12px 0;
    flex: 1;

    @include with-large-desktop {
      padding: 0 25px;
      border-left: 1px solid $gray-border;
    }
  }

  &__static-input {
    font-size: 14px;
    padding: 12px 10px;
    border-radius: 3px;
    background-color: $section-grey;
    min-width: 64px;
    height: 44px;
    text-align: center;
  }

  &__error-message {
    font-size: 14px;
    color: $red-light;
    margin-top: 5px;
    white-space: pre-line;
  }

  &__error-message-link {
    color: $red-light;
    font-weight: 700;

    &:hover,
    &:active {
      text-decoration: underline;
    }
  }

  &__free-note {
    padding-top: 10px;
    font-weight: 400;
    text-align: center;
  }

  &__link-to-dtf-artwork {
    text-decoration: underline;
    display: inline-block;
  }
}
</style>
